<template>
  <form class="form" novalidate @submit.prevent="onSubmit">
    <slot />
    <div
      v-if="hasButtonSlot || buttonText"
      ref="buttonWrapper"
      class="form__button-wrapper"
    >
      <!-- Scoped so custom #button slots (e.g. questionnaire Continue) can show loading -->
      <slot
        v-if="hasButtonSlot"
        name="button"
        :isLoading="isSubmitting || isRedirecting"
      />
      <FieldWrapper v-else-if="buttonText" class="field-wrapper--button">
        <Button
          :size="buttonSize"
          :color="buttonColor"
          :isDisabled="isDisabled"
          isKeepingColorWhenDisabled
          :isLoading="isSubmitting || isRedirecting"
        >
          {{ buttonText }}
        </Button>
      </FieldWrapper>
    </div>
  </form>
</template>

<script>
import { mapActions } from 'vuex'
import { smoothScrollTo } from '@/helpers/scroll'
import { getGqlErrorMessages } from '@/helpers/request'
import FieldWrapper from '@/components/Form/FieldWrapper.vue'
import Button, { BUTTON_STYLES } from '@/components/Form/Button.vue'
import { FIELD_MARGIN_TOP } from './field-settings'

export default {
  name: 'Form',
  components: {
    FieldWrapper,
    Button,
  },

  props: {
    getFieldComponents: {
      type: Function,
      required: true,
    },
    buttonText: {
      type: String,
      default: '',
    },
    buttonSize: {
      type: String,
      validator(value) {
        return Object.values(BUTTON_STYLES.size).includes(value)
      },
    },
    buttonColor: {
      type: String,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    handleSubmit: {
      type: Function,
      default: () => {},
    },
    beforeValidate: {
      type: Function,
      default: () => true,
    },
    isRedirecting: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      BUTTON_STYLES,
      isValidForm: true,
      isSubmitting: false,
      hasSubmitButtonDisabled: false,
    }
  },

  computed: {
    hasButtonSlot() {
      // Vue 2.6+: scoped and normal slots both appear on $scopedSlots
      return !!(this.$scopedSlots.button || this.$slots.button)
    },
  },

  methods: {
    ...mapActions({
      addFlashErrorMessage: 'flash/addErrorMessage',
    }),

    // used externally
    submit() {
      this.onSubmit()
    },

    isFieldValid(fieldComponent) {
      return !fieldComponent.hasError
    },

    validateForm() {
      let isValidForm = true
      const wasValidBeforeValidation = this.beforeValidate()
      const fieldComponents = this.getFieldComponents() || []

      fieldComponents.forEach(field => {
        if (process.browser && (!field.$el || !document.contains(field.$el))) return

        field.validate?.()
        if (isValidForm && !this.isFieldValid(field)) isValidForm = false
      })
      this.isValidForm = wasValidBeforeValidation && isValidForm
    },

    validateSubmitResponse(response) {
      if (!response?.error) return

      const fieldComponents = this.getFieldComponents() || []
      const errors = getGqlErrorMessages(response.error)
      errors.forEach(error => {
        const vars = error.vars || {}
        if (!error.field) return this.addFlashErrorMessage(this.$t(error.id, vars))

        const invalidField = fieldComponents.find(field => field.id === error.field)
        if (invalidField) {
          invalidField.addOneTimeError(this.$t(error.id, vars))
          this.isValidForm = false
        }
      })
      fieldComponents.forEach(field => (field.isValidatingOnChange = true))
    },

    async fixScrollPositionOnError(validationHandler) {
      const getSubmitButtonTop = () => this.$refs.buttonWrapper?.getBoundingClientRect().top
      const isDefined = v => typeof v !== 'undefined'
      const buttonTopBefore = getSubmitButtonTop()

      validationHandler()
      await this.$nextTick() // wait for the errors to be rendered

      if (!this.isValidForm) {
        const buttonTopAfter = getSubmitButtonTop()
        if (isDefined(buttonTopAfter) && isDefined(buttonTopBefore)) {
          window.scrollBy(0, buttonTopAfter - buttonTopBefore)
        }
      }
    },

    scrollToHighestError() {
      const highestErrorField = (this.getFieldComponents() || []).find(field => field.hasError)
      if (!highestErrorField) return

      let offset = -FIELD_MARGIN_TOP
      const el = highestErrorField.getHighestErrorField?.().$el || highestErrorField.$el
      const highestErrorFieldTop = el.getBoundingClientRect().top
      const previousSibling = highestErrorField.$el.previousElementSibling
      const headerHeight = document.querySelector('header')?.offsetHeight || 0

      if (previousSibling?.dataset?.fieldLabel) {
        offset -= previousSibling.offsetHeight + FIELD_MARGIN_TOP
        if (/^h2$/i.test(previousSibling.tagName)) offset -= 20
      }
      const scrollTo = window.pageYOffset - headerHeight + highestErrorFieldTop + offset

      if (window.pageYOffset > scrollTo) smoothScrollTo({ scrollTo })
    },

    async onSubmit() {
      if (this.isDisabled) return

      this.isSubmitting = true
      await this.fixScrollPositionOnError(this.validateForm)
      this.$emit('submit', this.isValidForm)

      if (!this.isValidForm) {
        this.scrollToHighestError()
        this.hasSubmitButtonDisabled = true
      } else {
        this.hasSubmitButtonDisabled = false
        const response = await this.handleSubmit()
        await this.fixScrollPositionOnError(() => this.validateSubmitResponse(response))
      }
      this.isSubmitting = false
    },
  },
}
</script>
